import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { BadgeProvider } from '../../hooks/useBadges';
import { Badge } from '../../components/ui/badge';
import { 
  Package, 
  PackagePlus,
  PackageMinus,
  Boxes,
  Truck,
  MessageSquare,
  Briefcase,
  Calendar
} from 'lucide-react';

// Depo Paneli bileşenlerini import et - aynı görünüm için
import DepotEntry from '../depot/DepotEntry';
import DepotExit from '../depot/DepotExit';
import DepotStock from '../depot/DepotStock';
import DepotIncoming from '../depot/DepotIncoming';
import DepotJobs from '../depot/DepotJobs';
import DepotCommunication from '../depot/DepotCommunication';

const API = process.env.REACT_APP_BACKEND_URL;

const BoardDepoContent = () => {
  const { branchId } = useParams();
  const { user } = useAuth();
  const [activeMenu, setActiveMenu] = useState('entry');
  const [branchName, setBranchName] = useState('');

  // Şube adını al
  React.useEffect(() => {
    const fetchBranchName = async () => {
      try {
        const token = localStorage.getItem('fortis_token');
        const response = await fetch(`${API}/api/branches`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const branches = await response.json();
          const branch = branches.find(b => b.id === branchId);
          setBranchName(branch?.name || 'Şube');
        }
      } catch (err) {
        console.error('Şube adı alınamadı:', err);
        setBranchName('Şube');
      }
    };
    fetchBranchName();
  }, [branchId]);

  // Menu Items - DepotPanel ile AYNI
  const menuItems = [
    { key: 'entry', icon: PackagePlus, label: 'Stok Giriş' },
    { key: 'exit', icon: PackageMinus, label: 'Stok Çıkış' },
    { key: 'stock', icon: Boxes, label: 'Depo Stok' },
    { key: 'incoming', icon: Truck, label: 'Gelecek Ürünler' },
    { key: 'jobs', icon: Briefcase, label: 'İşler' },
    { key: 'calendar', icon: Calendar, label: 'Takvim' },
    { key: 'communication', icon: MessageSquare, label: 'İletişim' },
  ];

  // İçerik render - Depo Paneli bileşenlerini doğrudan kullan
  const renderContent = () => {
    switch (activeMenu) {
      case 'entry':
        return <DepotEntry branchIdOverride={branchId} readOnly={true} />;
      case 'exit':
        return <DepotExit branchIdOverride={branchId} readOnly={true} />;
      case 'stock':
        return <DepotStock branchIdOverride={branchId} readOnly={true} />;
      case 'incoming':
        return <DepotIncoming branchIdOverride={branchId} readOnly={true} />;
      case 'jobs':
        return <DepotJobs branchIdOverride={branchId} readOnly={true} />;
      case 'calendar':
        return (
          <div className="text-center py-12 text-gray-500">
            <Calendar className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg">Takvim depo yöneticisi tarafından yönetilir</p>
          </div>
        );
      case 'communication':
        return <DepotCommunication branchIdOverride={branchId} readOnly={true} />;
      default:
        return <DepotEntry branchIdOverride={branchId} readOnly={true} />;
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="board-depo">
      {/* Header - DepotPanel ile aynı stil */}
      <header className="sticky top-0 z-40 border-b border-[hsl(var(--border))] bg-white/70 backdrop-blur">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Package className="h-8 w-8 text-emerald-600" />
              <span className="text-xl font-bold text-emerald-600">{branchName} - Depo</span>
            </div>
            <Badge variant="outline" className="ml-2 text-xs bg-amber-50 text-amber-700 border-amber-300">Salt Okunur</Badge>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-600">{user?.name}</span>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar - DepotPanel ile AYNI */}
        <aside className="sticky top-16 h-[calc(100vh-4rem)] w-56 border-r border-[hsl(var(--border))] bg-white p-4">
          <nav className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  onClick={() => setActiveMenu(item.key)}
                  className={`w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    activeMenu === item.key
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main Content - Depo Paneli bileşenlerini göster */}
        <main className="flex-1 p-6">
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

const BoardDepo = () => {
  return (
    <BadgeProvider panel="depot">
      <BoardDepoContent />
    </BadgeProvider>
  );
};

export default BoardDepo;
